

import React, { useState, useEffect } from 'react';
import { database, auth } from '../firebase';
import { ref, onValue, push, set, serverTimestamp, off } from 'firebase/database';
import { onAuthStateChanged } from 'firebase/auth';
import UnitsTable from './UnitsTable';
import AddUnitForm from './AddUnitForm';

const UnitsManager = () => {
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [editingUnit, setEditingUnit] = useState(null);

  const encodeEmailForPath = (email) => {
    return email.replace(/[.#$[\]]/g, '_');
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setCurrentUser(user);
      } else {
        setCurrentUser(null);
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!currentUser) {
      setLoading(false);
      return;
    }

    const encodedEmail = encodeEmailForPath(currentUser.email);
    const unitsRef = ref(database, `users/${encodedEmail}/units`);
    
    const fetchUnits = () => {
      onValue(unitsRef, (snapshot) => {
        const unitsData = [];
        snapshot.forEach((childSnapshot) => {
          const unitData = childSnapshot.val();
          unitsData.push({
            id: childSnapshot.key,
            name: unitData.name || '',
            createdAt: unitData.createdAt || null,
            createdBy: unitData.createdBy || 'N/A'
          });
        });
        unitsData.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
        setUnits(unitsData);
        setLoading(false);
      }, (error) => {
        console.error('Error fetching units: ', error);
        setError('Failed to load units. Please try again.');
        setLoading(false);
      });
    };

    fetchUnits();

    return () => {
      off(unitsRef);
    };
  }, [currentUser]);

  const handleAddUnit = () => {
    setError(null);
    setShowForm(true);
  };

  const handleUnitAdded = () => {
    setShowForm(false);
    setEditingUnit(null);
    setError(null);
  };

  const handleEditUnit = (unit) => {
    setEditingUnit(unit);
    setShowForm(true);
  };

  const handleDeleteUnit = (unitId) => {
    if (window.confirm('Are you sure you want to delete this unit?')) {
      if (!currentUser) return;
      
      const encodedEmail = encodeEmailForPath(currentUser.email);
      const unitRef = ref(database, `users/${encodedEmail}/units/${unitId}`);
      
      set(unitRef, null)
        .then(() => {
          // The onValue listener will automatically update the units state
        })
        .catch((error) => {
          console.error("Error deleting unit: ", error);
          setError('Failed to delete unit. Please try again.');
        });
    }
  };

  return (
    <div style={{
      maxWidth: '100%',
      margin: '0 auto',
      padding: '2rem',
      minHeight: '100vh',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    }}>
      {showForm ? (
        <AddUnitForm 
          onClose={() => {
            setShowForm(false);
            setEditingUnit(null);
          }}
          onUnitAdded={handleUnitAdded}
          currentUser={currentUser}
          editUnit={editingUnit}
        />
      ) : (
        <UnitsTable 
          units={units} 
          loading={loading}
          onAddUnit={handleAddUnit}
          onEditUnit={handleEditUnit}
          onDeleteUnit={handleDeleteUnit}
          currentUser={currentUser}
        />
      )}
    </div>
  );
};

export default UnitsManager;